import React from 'react';

import { ANCHOR_FROM_FO } from '@lumapps/contents/constants';
import { getCurrentContent } from '@lumapps/contents/ducks/selectors';
import { adminContent, contentView, homePageView } from '@lumapps/contents/routes';
import { LegacyContentStatus } from '@lumapps/contents/types';
import { CONTENT_EDITOR_TOPBAR_SCOPE } from '@lumapps/designer/constants';
import { getCurrentInstance } from '@lumapps/instance/ducks/selectors';
import { usePromiseConfirmDialog } from '@lumapps/lumx-dialogs/hooks/usePromiseConfirmDialog';
import { useSelector } from '@lumapps/redux/react';
import { useRouter } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { isLayoutEnabled as isLayoutEnabledSelector } from '@lumapps/widget-layout/ducks/selectors';

interface Params {
    /** Legacy content editor has changes */
    hasChanges?: () => boolean;
}

const SCOPE = `${CONTENT_EDITOR_TOPBAR_SCOPE}-close-confirm-dialog`;

/**
 * Callback when closing the content editor to determine where the user should be redirected.
 */
export function useCloseContentEditorRedirect(id?: string, params: Params = {}) {
    const { hasChanges } = params;
    const { redirect } = useRouter();
    const { translateKey, translateObject } = useTranslate();
    const instance = useSelector(getCurrentInstance);
    const isLayoutEnabled = useSelector(isLayoutEnabledSelector);
    const currentContent = useSelector(getCurrentContent);
    const hasALiveVersion = currentContent.status === LegacyContentStatus.live;
    const contentTypeId = currentContent.customContentTypeDetails?.id;
    const closeConfirmDialog = usePromiseConfirmDialog();

    // Check if coming from front-office (done once on mount)
    const fromFrontOffice = React.useMemo(() => {
        const fromFromOffice = window.location.hash === `#${ANCHOR_FROM_FO}`;
        if (fromFromOffice) {
            // Remove since it's not needed anymore
            window.location.hash = '';
        }
        return fromFromOffice;
    }, []);

    const closeContentEditor = React.useCallback(async () => {
        // Confirm close when content hasChanges
        if (hasChanges?.()) {
            const confirmed = await closeConfirmDialog.open({
                id: SCOPE,
                title: translateKey(GLOBAL.UNSAVED_MODIFICATIONS),
                confirmMessage: translateKey(GLOBAL.UNSAVED_MODIFICATIONS_DESCRIPTION),
                cancelProps: { label: translateKey(GLOBAL.CANCEL) },
                confirmProps: { label: translateKey(GLOBAL.CONTINUE) },
            });
            if (!confirmed) {
                // Cancel close
                return;
            }
        }

        // Has no live version (new content or has never been published)
        if (contentTypeId && !hasALiveVersion) {
            // Go to home page
            if (fromFrontOffice) {
                redirect(homePageView({}), { shouldSkipLegacyChangeConfirm: true });
                return;
            }

            // Go to the admin of contents for the content type
            redirect(adminContent(contentTypeId), { shouldSkipLegacyChangeConfirm: true });
            return;
        }

        // Go to content view
        redirect(
            contentView({
                isLayoutEnabled,
                to: {
                    id,
                    slug: translateObject(currentContent.slug) as string,
                    isV2Compatible: currentContent.template?.isV2Compatible,
                    isHomePage: id === instance?.homePage,
                },
            }),
            { shouldSkipLegacyChangeConfirm: true },
        );
    }, [
        hasChanges,
        contentTypeId,
        hasALiveVersion,
        redirect,
        isLayoutEnabled,
        id,
        translateObject,
        currentContent.slug,
        currentContent.template?.isV2Compatible,
        instance?.homePage,
        closeConfirmDialog,
        translateKey,
        fromFrontOffice,
    ]);

    return { closeContentEditor, closeConfirmDialogProps: closeConfirmDialog.props };
}
